import { z } from 'zod'

import { api } from './api.ts'
import type { SearchPage } from './page-search.ts'

/** Shorter questions cannot be classified by the server. */
export const MIN_ASK_LENGTH = 3

/** A display label, independent of the server's inclusion cutoff. */
export const LIKELY_PROBABILITY = 0.5

const answerSchema = z.object({
  status: z.enum(['ok', 'off', 'unavailable']),
  model: z.string().optional(),
  pages: z.array(
    z.object({
      id: z.string(),
      probability: z.number().min(0).max(1)
    })
  ),
  none: z.boolean(),
  reason: z.string().optional()
})

/** The validated response; failures carry no suggestions. */
export type SearchAskAnswer = z.infer<typeof answerSchema>

const UNAVAILABLE: SearchAskAnswer = {
  status: 'unavailable',
  pages: [],
  none: false
}

/** Normalize a question before debouncing, requesting, or caching it. */
export function normalizeAskQuery(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Treat an untrusted response as unavailable unless its known fields are valid.
 * An extra field is dropped, so an additive backend change does not hide Jev.
 */
export function parseSearchAsk(value: unknown): SearchAskAnswer {
  const parsed = answerSchema.safeParse(value)
  return parsed.success ? parsed.data : UNAVAILABLE
}

/**
 * Ask Jev through the authenticated API, without retries.
 *
 * A network error, an HTTP error (429 included) and a body that is not JSON
 * reject, so the cache never keeps a failure as an answer.
 */
export async function fetchSearchAsk(
  query: string,
  signal: AbortSignal
): Promise<SearchAskAnswer> {
  const body = await api
    .post('search-ask', { json: { query }, signal, retry: 0 })
    .json<unknown>()
  return parseSearchAsk(body)
}

/** Describe probability without exposing its number or filtering any server result. */
export function likelihoodWord(probability: number): 'likely' | 'possible' {
  return probability >= LIKELY_PROBABILITY ? 'likely' : 'possible'
}

/** A known page with its server probability. */
export interface SuggestedPage extends SearchPage {
  probability: number
}

/** Keep the viewer's known pages in server order, once each; a none answer has no rows. */
export function suggestedPages(
  answer: SearchAskAnswer | undefined,
  pages: readonly SearchPage[]
): SuggestedPage[] {
  if (answer?.status !== 'ok' || answer.none) return []
  const known = new Map(pages.map(entry => [entry.page.id, entry]))
  const seen = new Set<string>()
  return answer.pages.flatMap(result => {
    const entry = known.get(result.id)
    if (entry === undefined || seen.has(result.id)) return []
    seen.add(result.id)
    return [{ ...entry, probability: result.probability }]
  })
}
